import { apiFetch } from '../../shared/apiClient';
import type {
  Commitment,
  CommitmentBaselineIssue,
  CommitmentView,
  CommitmentSnapshot,
  CommitmentComment,
  CommitmentSources,
  CommitmentSourceType,
  CommitmentSourceConfigFull,
  CommitmentStatus,
  ArchivedCommitmentSummary,
  JiraField,
} from '../../shared/types';
import type { CommitmentScoreBreakdown } from '../../domain/commitment-scoring';

const API_PREFIX = '/api/commitments';

export const CommitmentService = {
  async getSources(purpose: 'scope' | 'delivery' = 'scope'): Promise<CommitmentSources> {
    const q = purpose === 'delivery' ? '?purpose=delivery' : '';
    const response = await apiFetch(`${API_PREFIX}/sources${q}`);
    if (!response.ok) {
      throw new Error('Failed to fetch commitment sources');
    }
    return response.json();
  },

  async getActiveCommitment(): Promise<Commitment | null> {
    const response = await apiFetch(`${API_PREFIX}/active`);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch active commitment');
    }
    return response.json();
  },

  async listCommitments(options?: { status?: CommitmentStatus }): Promise<Commitment[]> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    const qs = params.toString();
    const url = qs ? `${API_PREFIX}?${qs}` : API_PREFIX;
    const response = await apiFetch(url);
    if (!response.ok) {
      throw new Error('Failed to fetch commitments');
    }
    const data = await response.json();
    return data.commitments || [];
  },

  async listByStatus(status: CommitmentStatus): Promise<Commitment[]> {
    return CommitmentService.listCommitments({ status });
  },

  async getArchivedList(params?: {
    page?: number;
    pageSize?: number;
    sort?: string;
    order?: string;
    search?: string;
  }): Promise<{ items: ArchivedCommitmentSummary[]; total: number }> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.pageSize) query.set('pageSize', String(params.pageSize));
    if (params?.sort) query.set('sort', params.sort);
    if (params?.order) query.set('order', params.order);
    if (params?.search) query.set('search', params.search);
    const response = await apiFetch(`${API_PREFIX}/archived?${query.toString()}`);
    if (!response.ok) {
      throw new Error('Failed to fetch archived commitments');
    }
    const data = await response.json();
    return {
      items: Array.isArray(data.items) ? data.items : (data.archived ?? []),
      total: data.total ?? data.archived?.length ?? data.items?.length ?? 0,
    };
  },

  async deleteCommitment(id: number): Promise<void> {
    const response = await apiFetch(`${API_PREFIX}/${id}`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to delete commitment');
    }
  },

  async releaseCommitment(id: number): Promise<Commitment> {
    const response = await apiFetch(`${API_PREFIX}/${id}/release`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to release commitment');
    }
    return response.json();
  },

  async archiveCommitment(id: number): Promise<Commitment> {
    const response = await apiFetch(`${API_PREFIX}/${id}/archive`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to archive commitment');
    }
    return response.json();
  },

  async reactivateCommitment(id: number): Promise<Commitment> {
    const response = await apiFetch(`${API_PREFIX}/${id}/reactivate`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to reactivate commitment');
    }
    return response.json();
  },

  async checkJiraReleaseStatus(id: number): Promise<{ released: boolean }> {
    const response = await apiFetch(`${API_PREFIX}/${id}/jira-release-status`);
    if (!response.ok) {
      throw new Error('Failed to check Jira release status');
    }
    return response.json();
  },

  async createCommitment(payload: {
    sourceType: CommitmentSourceType;
    sourceValue: string;
    targetDate?: string;
    deliveryType?: string;
    deliveryValue?: string;
  }): Promise<Commitment & { baselineIssues: CommitmentBaselineIssue[] }> {
    const response = await apiFetch(`${API_PREFIX}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to create commitment');
    }
    return response.json();
  },

  /** Backfill baseline rows for commitments created before server populated baseline (empty → current scope). */
  async reseedBaseline(
    id: number
  ): Promise<Commitment & { baselineIssues: CommitmentBaselineIssue[] }> {
    const response = await apiFetch(`${API_PREFIX}/${id}/reseed-baseline`, { method: 'POST' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to reseed baseline');
    }
    return response.json();
  },

  async getCommitmentView(id: number): Promise<CommitmentView> {
    const response = await apiFetch(`${API_PREFIX}/${id}/view`);
    if (!response.ok) {
      throw new Error('Failed to fetch commitment view');
    }
    return response.json();
  },

  async updateCommitment(
    id: number,
    payload: { status?: string; targetDate?: string | null }
  ): Promise<Commitment> {
    const response = await apiFetch(`${API_PREFIX}/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to update commitment');
    }
    return response.json();
  },

  async refreshSnapshot(id: number): Promise<{ snapshot: CommitmentSnapshot }> {
    const response = await apiFetch(`${API_PREFIX}/${id}/snapshot`, {
      method: 'POST',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to create snapshot');
    }
    return response.json();
  },

  async getSnapshots(id: number): Promise<{ snapshots: CommitmentSnapshot[] }> {
    const response = await apiFetch(`${API_PREFIX}/${id}/snapshots`);
    if (!response.ok) {
      throw new Error('Failed to fetch snapshots');
    }
    return response.json();
  },

  async getIssueComments(
    commitmentId: number,
    issueKey: string
  ): Promise<{ comments: CommitmentComment[] }> {
    const response = await apiFetch(
      `${API_PREFIX}/${commitmentId}/issues/${encodeURIComponent(issueKey)}/comments`
    );
    if (!response.ok) {
      throw new Error('Failed to fetch issue comments');
    }
    return response.json();
  },

  async addIssueComment(
    commitmentId: number,
    issueKey: string,
    text: string,
    author?: string
  ): Promise<CommitmentComment> {
    const response = await apiFetch(
      `${API_PREFIX}/${commitmentId}/issues/${encodeURIComponent(issueKey)}/comments`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, author }),
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to add comment');
    }
    return response.json();
  },

  async updateIssueComment(
    commitmentId: number,
    issueKey: string,
    commentId: number,
    text: string
  ): Promise<CommitmentComment> {
    const response = await apiFetch(
      `${API_PREFIX}/${commitmentId}/issues/${encodeURIComponent(issueKey)}/comments/${commentId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to update comment');
    }
    return response.json();
  },

  async deleteIssueComment(
    commitmentId: number,
    issueKey: string,
    commentId: number
  ): Promise<void> {
    const response = await apiFetch(
      `${API_PREFIX}/${commitmentId}/issues/${encodeURIComponent(issueKey)}/comments/${commentId}`,
      {
        method: 'DELETE',
      }
    );
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to delete comment');
    }
  },

  async getComments(commitmentId: number): Promise<{ comments: CommitmentComment[] }> {
    const response = await apiFetch(`${API_PREFIX}/${commitmentId}/comments`);
    if (!response.ok) {
      throw new Error('Failed to fetch comments');
    }
    return response.json();
  },

  async addComment(
    commitmentId: number,
    text: string,
    author?: string
  ): Promise<CommitmentComment> {
    const response = await apiFetch(`${API_PREFIX}/${commitmentId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, author }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to add comment');
    }
    return response.json();
  },

  async updateComment(
    commitmentId: number,
    commentId: number,
    text: string
  ): Promise<CommitmentComment> {
    const response = await apiFetch(`${API_PREFIX}/${commitmentId}/comments/${commentId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to update comment');
    }
    return response.json();
  },

  async deleteComment(commitmentId: number, commentId: number): Promise<void> {
    const response = await apiFetch(`${API_PREFIX}/${commitmentId}/comments/${commentId}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to delete comment');
    }
  },

  async updateDoneStatuses(statuses: string[]): Promise<void> {
    const response = await apiFetch(`${API_PREFIX}/config/done-statuses`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ statuses }),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to update done statuses');
    }
  },

  async getSourceConfigs(): Promise<CommitmentSourceConfigFull[]> {
    const response = await apiFetch('/api/commitment-source-configs');
    if (!response.ok) {
      throw new Error('Failed to fetch source configs');
    }
    const data = await response.json();
    return data.configs || [];
  },

  async addSourceConfig(payload: {
    displayName: string;
    jiraFieldId: string;
    matchMode?: string;
    /** Forward Jira's `/rest/api/3/field` schema so the server can
     *  auto-detect a sensible field_kind for org_custom_fields. */
    schema?: Record<string, unknown> | null;
  }): Promise<CommitmentSourceConfigFull> {
    const response = await apiFetch('/api/commitment-source-configs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to add source config');
    }
    return response.json();
  },

  async updateSourceConfig(
    id: number,
    payload: {
      enabled?: boolean;
      deliveryEnabled?: boolean;
      sortOrder?: number;
      icon?: string;
      displayName?: string;
    }
  ): Promise<CommitmentSourceConfigFull> {
    const response = await apiFetch(`/api/commitment-source-configs/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to update source config');
    }
    return response.json();
  },

  async deleteSourceConfig(id: number): Promise<void> {
    const response = await apiFetch(`/api/commitment-source-configs/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to delete source config');
    }
  },

  /**
   * Copy the ACTIVE product's source config (builtin toggles AND custom
   * source types) to every other product in the org. The server iterates
   * products inside a transaction and invalidates downstream caches per
   * product. Returns the number of OTHER products written.
   */
  async applySourceConfigsToAll(): Promise<{ appliedTo: number; productIds: string[] }> {
    const response = await apiFetch('/api/commitment-source-configs/apply-to-all', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || 'Failed to apply source configs to all products');
    }
    return response.json();
  },

  async getScoreBreakdown(id: number): Promise<CommitmentScoreBreakdown> {
    const response = await apiFetch(`${API_PREFIX}/${id}/score-breakdown`);
    if (!response.ok) {
      throw new Error('Failed to fetch score breakdown');
    }
    return response.json();
  },

  async getJiraFields(): Promise<JiraField[]> {
    const response = await apiFetch('/api/jira/fields');
    if (!response.ok) {
      throw new Error('Failed to fetch Jira fields');
    }
    const data = await response.json();
    // The server hands back the raw Jira `/rest/api/3/field` array; tolerate
    // both that shape and `{ fields: [...] }` for forward-compat. Without
    // this fallback the dropdown is always empty (data.fields is undefined
    // on a raw array), which is exactly what made "Target Version" invisible
    // in the Add Custom Source Type form.
    const list: unknown[] = Array.isArray(data) ? data : data?.fields || [];
    return list
      .map((raw) => {
        const f = raw as {
          id?: unknown;
          key?: unknown;
          name?: unknown;
          custom?: unknown;
          schema?: unknown;
        };
        const id = typeof f.id === 'string' ? f.id : typeof f.key === 'string' ? f.key : '';
        const name = typeof f.name === 'string' ? f.name : id;
        if (!id) return null;
        return {
          id,
          name,
          custom: typeof f.custom === 'boolean' ? f.custom : id.startsWith('customfield_'),
          schema:
            f.schema && typeof f.schema === 'object' ? (f.schema as Record<string, unknown>) : null,
        } as JiraField;
      })
      .filter((f): f is JiraField => f !== null);
  },
};

export default CommitmentService;
